import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Apple as AppleIcon,
  Google as GoogleIcon,
  Security as SecurityIcon,
  Payment as PaymentTestIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  PlatformSettingsService,
  PlatformPaymentMethod,
  CreatePlatformPaymentMethodData,
} from '../../../services/platformSettingsService';
import CompleteTransactionPopup from './CompleteTransactionPopup';

const AdminPaymentMethodsTab: React.FC = () => {
  const [paymentMethods, setPaymentMethods] = useState<PlatformPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PlatformPaymentMethod | null>(null);
  const [testPaymentDialogOpen, setTestPaymentDialogOpen] = useState(false);
  const [newMethod, setNewMethod] = useState({
    type: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: '',
  });

  // Load payment methods on component mount
  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const methods = await PlatformSettingsService.getPlatformPaymentMethods();
      setPaymentMethods(methods);
    } catch (error) {
      console.error('Error loading platform payment methods:', error);
      toast.error('Failed to load platform payment methods');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCardIcon />;
      case 'bank':
        return <BankIcon />;
      case 'paypal':
        return (
          <Box
            component="img"
            src="/Paypal_2014_logo.png"
            alt="PayPal"
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
            }}
          />
        );
      case 'apple':
        return <AppleIcon />;
      case 'google':
        return <GoogleIcon />;
      default:
        return <CreditCardIcon />;
    }
  };

  const getPaymentColor = (type: string) => {
    switch (type) {
      case 'card':
        return '#1976D2';
      case 'bank':
        return '#4CAF50';
      case 'paypal':
        return '#0070BA';
      case 'apple':
        return '#000000';
      case 'google':
        return '#4285F4';
      default:
        return '#1976D2';
    }
  };

  const handleAddMethod = async () => {
    // Prevent adding if there's already a payment method
    if (paymentMethods.length >= 1) {
      toast.error('Only one platform payment method is allowed');
      return;
    }

    if (newMethod.type === 'card' && (!newMethod.cardNumber || !newMethod.expiryDate || !newMethod.cvv || !newMethod.name)) {
      toast.error('Please fill in all card details');
      return;
    }

    if (newMethod.type === 'paypal' && (!newMethod.email || !newMethod.name)) {
      toast.error('Please fill in PayPal email and account name');
      return;
    }

    try {
      const methodData: CreatePlatformPaymentMethodData = {
        payment_method_type: newMethod.type as any,
        payment_method_name: newMethod.type === 'card' 
          ? `${newMethod.name} •••• ${newMethod.cardNumber.slice(-4)}` 
          : newMethod.name,
        payment_method_last4: newMethod.type === 'card' ? newMethod.cardNumber.slice(-4) : undefined,
        payment_method_expiry: newMethod.type === 'card' ? newMethod.expiryDate : undefined,
        payment_method_email: newMethod.type === 'paypal' ? newMethod.email : undefined,
        payment_method_is_default: true, // Always default since there can only be one
      };

      await PlatformSettingsService.addPlatformPaymentMethod(methodData);
      await loadPaymentMethods();
      
      setAddDialogOpen(false);
      setNewMethod({
        type: 'card',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        name: '',
        email: '',
      });
      toast.success('Platform payment method added successfully!');
    } catch (error) {
      console.error('Error adding platform payment method:', error);
      toast.error('Failed to add platform payment method');
    }
  };

  const handleEditMethod = (method: PlatformPaymentMethod) => {
    setEditingMethod(method);
    setNewMethod({
      type: method.payment_method_type,
      cardNumber: method.payment_method_last4 ? `**** **** **** ${method.payment_method_last4}` : '',
      expiryDate: method.payment_method_expiry || '',
      cvv: '',
      name: method.payment_method_type === 'card' 
        ? method.payment_method_name.split(' ••••')[0] 
        : method.payment_method_name,
      email: method.payment_method_email || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateMethod = async () => {
    if (!editingMethod) return;

    if (newMethod.type === 'card' && (!newMethod.cardNumber || !newMethod.expiryDate || !newMethod.cvv || !newMethod.name)) {
      toast.error('Please fill in all card details');
      return;
    }

    if (newMethod.type === 'paypal' && (!newMethod.email || !newMethod.name)) {
      toast.error('Please fill in PayPal email and account name');
      return;
    }

    try {
      const updates: Partial<CreatePlatformPaymentMethodData> = {
        payment_method_name: newMethod.type === 'card' 
          ? `${newMethod.name} •••• ${newMethod.cardNumber.slice(-4)}` 
          : newMethod.name,
        payment_method_last4: newMethod.type === 'card' ? newMethod.cardNumber.slice(-4) : undefined,
        payment_method_expiry: newMethod.type === 'card' ? newMethod.expiryDate : undefined,
        payment_method_email: newMethod.type === 'paypal' ? newMethod.email : undefined,
      };

      await PlatformSettingsService.updatePlatformPaymentMethod(editingMethod.id, updates);
      await loadPaymentMethods();
      
      setEditDialogOpen(false);
      setEditingMethod(null);
      setNewMethod({
        type: 'card',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        name: '',
        email: '',
      });
      toast.success('Platform payment method updated successfully!');
    } catch (error) {
      console.error('Error updating platform payment method:', error);
      toast.error('Failed to update platform payment method');
    }
  };

  const handleDeleteMethod = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this platform payment method?')) {
      return;
    }

    try {
      await PlatformSettingsService.deletePlatformPaymentMethod(id);
      await loadPaymentMethods();
      toast.success('Platform payment method removed successfully!');
    } catch (error) {
      console.error('Error deleting platform payment method:', error);
      toast.error('Failed to remove platform payment method');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await PlatformSettingsService.setDefaultPlatformPaymentMethod(id);
      await loadPaymentMethods();
      toast.success('Default platform payment method updated!');
    } catch (error) {
      console.error('Error setting default platform payment method:', error);
      toast.error('Failed to set default platform payment method');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
            Platform Payment Methods
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage the platform payment method for receiving platform fees. Only one payment method is allowed.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PaymentTestIcon />}
            onClick={() => setTestPaymentDialogOpen(true)}
            sx={{
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': {
                borderColor: '#45a049',
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
              },
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Test Payment
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={paymentMethods.length >= 1}
            sx={{
              background: paymentMethods.length >= 1 
                ? 'rgba(0, 0, 0, 0.12)' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: paymentMethods.length >= 1 ? 'rgba(0, 0, 0, 0.38)' : 'white',
              '&:hover': {
                background: paymentMethods.length >= 1 
                  ? 'rgba(0, 0, 0, 0.12)' 
                  : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.38)',
              },
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
            }}
          >
            Add Payment Method
          </Button>
        </Box>
      </Box>

      {paymentMethods.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed rgba(0, 0, 0, 0.1)',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          }}
        >
          <CreditCardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            No Payment Methods
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Add a payment method to receive platform fees from transactions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={paymentMethods.length >= 1}
            sx={{
              background: paymentMethods.length >= 1 
                ? 'rgba(0, 0, 0, 0.12)' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: paymentMethods.length >= 1 ? 'rgba(0, 0, 0, 0.38)' : 'white',
              '&:hover': {
                background: paymentMethods.length >= 1 
                  ? 'rgba(0, 0, 0, 0.12)' 
                  : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.38)',
              },
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            Add Your First Payment Method
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {paymentMethods.map((method, index) => (
            <motion.div
              key={method.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  border: method.payment_method_is_default ? '2px solid #667eea' : '1px solid rgba(0, 0, 0, 0.05)',
                  background: method.payment_method_is_default 
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                {method.payment_method_is_default && (
                  <Chip
                    label="Default"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: 16,
                      backgroundColor: '#667eea',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: `${getPaymentColor(method.payment_method_type)}20`,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {getPaymentIcon(method.payment_method_type)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {method.payment_method_name}
                      </Typography>
                      {method.payment_method_expiry && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Expires {method.payment_method_expiry}
                        </Typography>
                      )}
                      {method.payment_method_email && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {method.payment_method_email}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditMethod(method)}
                      sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteMethod(method.id)}
                      sx={{
                        textTransform: 'none',
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                        },
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 4,
          borderRadius: 3,
          border: '1px solid rgba(0, 0, 0, 0.05)',
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SecurityIcon sx={{ color: '#4CAF50', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Platform Settings
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Only one platform payment method is allowed. This payment method is shared across all admin users 
          and will be used to receive platform fees from all transactions.
        </Typography>
      </Paper>

      {/* Add Payment Method Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Add Platform Payment Method
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Payment Method Type"
              value={newMethod.type}
              onChange={(e) => setNewMethod(prev => ({ ...prev, type: e.target.value as any }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="card">Credit/Debit Card</option>
              <option value="paypal">PayPal Account</option>
            </TextField>

            {newMethod.type === 'card' ? (
              <>
                <TextField
                  fullWidth
                  label="Card Number"
                  value={newMethod.cardNumber}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, cardNumber: e.target.value }))}
                  placeholder="1234 5678 9012 3456"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Expiry Date"
                    value={newMethod.expiryDate}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, expiryDate: e.target.value }))}
                    placeholder="MM/YY"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth
                    label="CVV"
                    value={newMethod.cvv}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, cvv: e.target.value }))}
                    placeholder="123"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Cardholder Name"
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="PayPal Email"
                  type="email"
                  value={newMethod.email}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@example.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Account Name"
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Platform PayPal Account"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddMethod}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            Add Method
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Method Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Edit Platform Payment Method
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Payment Method Type"
              value={newMethod.type}
              onChange={(e) => setNewMethod(prev => ({ ...prev, type: e.target.value as any }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="card">Credit/Debit Card</option>
              <option value="paypal">PayPal Account</option>
            </TextField>

            {newMethod.type === 'card' ? (
              <>
                <TextField
                  fullWidth
                  label="Card Number"
                  value={newMethod.cardNumber}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, cardNumber: e.target.value }))}
                  placeholder="1234 5678 9012 3456"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Expiry Date"
                    value={newMethod.expiryDate}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, expiryDate: e.target.value }))}
                    placeholder="MM/YY"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth
                    label="CVV"
                    value={newMethod.cvv}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, cvv: e.target.value }))}
                    placeholder="123"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Cardholder Name"
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="PayPal Email"
                  type="email"
                  value={newMethod.email}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@example.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Account Name"
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Platform PayPal Account"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateMethod}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            Update Method
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Payment Dialog */}
      <CompleteTransactionPopup
        open={testPaymentDialogOpen}
        onClose={() => setTestPaymentDialogOpen(false)}
      />
    </Box>
  );
};

export default AdminPaymentMethodsTab;

